import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, User, Phone, MessageSquare, CheckCircle, AlertCircle, Loader, Search, Filter, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import ReagendarVisitaModal from '../../components/shared/ReagendarVisitaModal';

function AgendaCalDiy() {
  const [disponibilidade, setDisponibilidade] = useState([]);
  const [agendamentos, setAgendamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedEmpreendimento, setSelectedEmpreendimento] = useState('ingarden-decorado');
  const [leadSelecionado, setLeadSelecionado] = useState(null);
  const [buscaLead, setBuscaLead] = useState('');
  const [leadsEncontrados, setLeadsEncontrados] = useState([]);
  const [observacoes, setObservacoes] = useState('');
  const [agendando, setAgendando] = useState(false);
  const [statusAgendamento, setStatusAgendamento] = useState(null);
  const [reagendando, setReagendando] = useState(null);

  const empreendimentos = [
    { id: 'ingarden-decorado', nome: 'Ingarden - Decorado' },
    { id: 'lumiere-residence', nome: 'Lumiere Residence Club' },
    { id: 'benjamin-constant', nome: 'Benjamin Constant Duplex' }
  ];

  useEffect(() => {
    carregarAgendamentos();
    const hoje = new Date();
    setSelectedDate(hoje.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (selectedDate) {
      carregarDisponibilidade();
    }
  }, [selectedDate, selectedEmpreendimento]);

  const carregarDisponibilidade = async () => {
    try {
      const response = await api.get(`/sdr-voz/agenda/disponibilidade`, {
        params: { data: selectedDate, empreendimento: selectedEmpreendimento }
      });
      setDisponibilidade(response.data.data?.horarios || []);
    } catch (err) {
      console.error('Erro ao carregar disponibilidade:', err);
    }
  };

  const carregarAgendamentos = async () => {
    setLoading(true);
    try {
      // Visitas reais agendadas no cal.diy (não confundir com /agendamentos, que são
      // mensagens de WhatsApp agendadas — endpoint errado que estava aqui antes)
      const response = await api.get('/visitas');
      setAgendamentos(response.data.data || []);
    } catch (err) {
      console.error('Erro ao carregar agendamentos:', err);
    } finally {
      setLoading(false);
    }
  };

  const buscarLeads = async (termo) => {
    if (!termo.trim()) {
      setLeadsEncontrados([]);
      return;
    }

    try {
      const response = await api.get('/leads', {
        params: { busca: termo, limite: 10 }
      });
      setLeadsEncontrados(response.data.data || []);
    } catch (err) {
      console.error('Erro ao buscar leads:', err);
    }
  };

  const selecionarLead = (lead) => {
    setLeadSelecionado(lead);
    setBuscaLead(`${lead.nome} - ${lead.telefone}`);
    setLeadsEncontrados([]);
  };

  const agendarVisita = async () => {
    if (!leadSelecionado || !selectedTime) {
      setStatusAgendamento({ type: 'error', message: 'Selecione um lead e um horário' });
      return;
    }

    setAgendando(true);
    setStatusAgendamento(null);

    try {
      const response = await api.post('/sdr-voz/agenda/agendar', {
        lead_id: leadSelecionado.id,
        numero: leadSelecionado.telefone,
        nome: leadSelecionado.nome,
        empreendimento: selectedEmpreendimento,
        data: selectedDate,
        horario: selectedTime,
        observacoes: observacoes
      });

      setStatusAgendamento({ type: 'success', message: 'Agendamento realizado com sucesso!' });
      setLeadSelecionado(null);
      setBuscaLead('');
      setSelectedTime('');
      setObservacoes('');
      carregarAgendamentos();
    } catch (err) {
      setStatusAgendamento({ type: 'error', message: 'Erro ao agendar: ' + (err.response?.data?.error || err.message) });
    } finally {
      setAgendando(false);
    }
  };

  const formatarHorario = (horario) => {
    return horario.substring(0, 5).replace(':', 'h');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Agendamento de Visitas</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Agende visitas ao decorado através da integração com Cal.diy
          </p>
        </div>
        <button onClick={carregarAgendamentos} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
          <RefreshCw size={16} /> Atualizar
        </button>
      </div>

      {/* Status Agendamento */}
      {statusAgendamento && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg ${
          statusAgendamento.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {statusAgendamento.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {statusAgendamento.message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulário de Agendamento */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow p-5 space-y-4">
            <h2 className="font-semibold text-gray-700 text-lg">Novo Agendamento</h2>

            {/* Seleção de Empreendimento */}
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                <MapPin size={14} className="inline mr-1" /> Empreendimento
              </label>
              <select
                value={selectedEmpreendimento}
                onChange={(e) => setSelectedEmpreendimento(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {empreendimentos.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.nome}</option>
                ))}
              </select>
            </div>

            {/* Data */}
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                <Calendar size={14} className="inline mr-1" /> Data
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            {/* Busca de Lead */}
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                <User size={14} className="inline mr-1" /> Lead
              </label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={buscaLead}
                  onChange={(e) => {
                    setBuscaLead(e.target.value);
                    buscarLeads(e.target.value);
                  }}
                  placeholder="Buscar por nome ou telefone..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* Resultados da busca */}
              {leadsEncontrados.length > 0 && (
                <div className="mt-2 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                  {leadsEncontrados.map(lead => (
                    <div
                      key={lead.id}
                      className="px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => selecionarLead(lead)}
                    >
                      <p className="text-sm font-medium text-gray-800">{lead.nome}</p>
                      <p className="text-xs text-gray-500 font-mono">{lead.telefone}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Horário */}
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                <Clock size={14} className="inline mr-1" /> Horário
              </label>
              <div className="grid grid-cols-3 gap-2">
                {disponibilidade.map(horario => (
                  <button
                    key={horario}
                    onClick={() => setSelectedTime(horario)}
                    disabled={!leadSelecionado}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                      selectedTime === horario
                        ? 'border-primary bg-primary text-white'
                        : 'border-gray-200 text-gray-700 hover:border-blue-300'
                    } ${!leadSelecionado ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {formatarHorario(horario)}
                  </button>
                ))}
              </div>
            </div>

            {/* Observações */}
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                <MessageSquare size={14} className="inline mr-1" /> Observações
              </label>
              <textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                placeholder="Informações adicionais sobre a visita..."
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
              />
            </div>

            {/* Botão de Agendamento */}
            <button
              onClick={agendarVisita}
              disabled={agendando || !leadSelecionado || !selectedTime}
              className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-lg font-semibold text-sm hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {agendando ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Agendando...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Agendar Visita
                </>
              )}
            </button>
          </div>
        </div>

        {/* Disponibilidade e Agendamentos */}
        <div className="lg:col-span-2 space-y-6">
          {/* Disponibilidade */}
          <div className="bg-white rounded-xl shadow p-5">
            <h2 className="font-semibold text-gray-700 text-lg mb-4">Disponibilidade - {selectedDate}</h2>

            {disponibilidade.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Calendar size={48} className="mx-auto mb-2 opacity-40" />
                <p>Nenhum horário disponível para esta data</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {disponibilidade.map(horario => (
                  <div
                    key={horario}
                    className={`px-3 py-3 rounded-lg text-center border-2 ${
                      selectedTime === horario
                        ? 'border-primary bg-primary text-white'
                        : 'border-gray-200 text-gray-700 hover:border-blue-300'
                    }`}
                  >
                    {formatarHorario(horario)}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Próximos Agendamentos */}
          <div className="bg-white rounded-xl shadow p-5">
            <h2 className="font-semibold text-gray-700 text-lg mb-4">Próximos Agendamentos</h2>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader size={24} className="animate-spin" />
              </div>
            ) : agendamentos.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Calendar size={48} className="mx-auto mb-2 opacity-40" />
                <p>Nenhum agendamento encontrado</p>
              </div>
            ) : (
              <div className="space-y-3">
                {agendamentos.slice(0, 10).map(agendamento => (
                  <div key={agendamento.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Calendar size={20} className="text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{agendamento.attendee_name || agendamento.title}</p>
                          <p className="text-sm text-gray-500">{agendamento.attendee_phone}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-700">
                          {new Date(agendamento.startTime).toLocaleDateString('pt-BR')} às{' '}
                          {new Date(agendamento.startTime).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <p className="text-xs text-gray-500">{agendamento.location}</p>
                        <button
                          onClick={() => setReagendando(agendamento)}
                          className="mt-1 text-xs text-blue-600 hover:underline"
                        >
                          Reagendar
                        </button>
                      </div>
                    </div>
                    {agendamento.description && (
                      <p className="text-sm text-gray-600 mt-2 pl-13">{agendamento.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <ReagendarVisitaModal
        isOpen={!!reagendando}
        onClose={() => setReagendando(null)}
        onSuccess={() => {
          setReagendando(null);
          carregarAgendamentos();
        }}
        bookingId={reagendando?.id}
        nome={reagendando?.attendee_name || reagendando?.title}
        startTime={reagendando?.startTime}
        empreendimento={reagendando?.location}
      />
    </div>
  );
}

export default AgendaCalDiy;